import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	InputNumber,
	Modal,
	notification,
	Tag,
	Tooltip,
} from 'antd';
import { useNavigate } from 'react-router';
import { MaterialReservation } from '../../../../../interfaces/MaterialInventory.interface';
import { UserListInterface } from '../../../../../interfaces/User.interface.ts';
import CustomResponsiveTable from '../../../../../components/CustomResponsiveTable';
import {
	usePrintCutTagMutation,
	useUpdateMultipleMaterialReservationsToCutStatusMutation,
} from '../../../../../redux/api/material';
import {
	useGetUsersQuery,
	UserQueryInterface,
} from '../../../../../redux/api/users.ts';
import MaterialChecklist from '../MaterialChecklist';
import OperatorPinField from '../../../../Operators/OperatorPinField';
import { getPredictedRemainingLength } from '../../../../../utils/cutCalculation';
import {
	calculateScrapStatus,
	qualifiesAsScrapForBulk,
} from './utils/scrapLogic';
import MaterialLocationsSummaryModal from '../MaterialLocationsSummaryModal';
import { usePrintLocation } from '../../../../../context/PrintLocationContext';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { useIdempotencyKey } from '../../../../../utils/idempotency';
import { barBlockingReason } from '../../queueClassification';

interface ICutItems {
	material_reservation: string;
	actual_cut_quantity: number;
	remaining_length: string;
	cut_by?: number | string | null;
	material_checklist_accepted?: boolean;
	is_scrap?: boolean; // Flag to indicate if material should be scrapped
}

/**
 * Mirrors `MaterialReservation.get_group_key()` on the backend and the SQL
 * `Concat(order_line_item, material, material_cut_length)` annotation used by
 * `MaterialReservationsViewSet.get_queryset_grouped`. PostgreSQL CONCAT treats
 * NULL as empty string so we do the same here. Returns null when required
 * fields are missing (which disables "Save and Print" gracefully).
 */
function deriveGroupKey(reservation: any): string | null {
	if (!reservation) return null;
	if (reservation.group_key) return reservation.group_key;

	const orderLineItemId =
		reservation.order_line_item?.id ?? reservation.order_line_item ?? '';
	const material = reservation.material_length?.material;
	const materialId = material?.id ?? material ?? null;
	const cutLengthRaw = reservation.material_cut_length;

	if (!materialId || cutLengthRaw === null || cutLengthRaw === undefined) {
		return null;
	}

	const cutLengthFormatted = Number(cutLengthRaw).toFixed(2);
	return `${orderLineItemId}${materialId}${cutLengthFormatted}`;
}

interface IProps {
	visible: boolean;
	onCancel: () => void;
	materialReservations: MaterialReservation[];
	overrideMaterialReservations: (values: MaterialReservation[]) => void;
	loadingMaterialReservations: boolean;
	userData?: UserListInterface[];
	setRadioButtonLoading?: (value: boolean) => void;
	refetch: () => void;
	handleFetchReservationsById?: (value: string) => void;
	defaultUser?: string | number;
}


const CutMaterialModal: React.FC<IProps> = ({
	visible,
	materialReservations: rawMaterialReservations,
	overrideMaterialReservations,
	loadingMaterialReservations,
	userData: userDataProp,
	setRadioButtonLoading,
	refetch,
	handleFetchReservationsById,
	defaultUser,
	onCancel,
}) => {
	const [isCutting, setIsCutting] = useState<boolean>(false);
	const [selectedUser, setSelectedUser] = useState<string | number | undefined>(
		undefined
	);
	const [operatorTouched, setOperatorTouched] = useState<boolean>(false);
	const [cutMaterialReservationValues, setCutMaterialReservationValues] =
		useState<ICutItems[]>([]);

	// Material Locations Summary Modal state
	const [showLocationSummary, setShowLocationSummary] =
		useState<boolean>(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: any;
		lengths: any[];
	}>({
		materialInfo: {},
		lengths: [],
	});
	const [savedGroupKey, setSavedGroupKey] = useState<string>('');

	const navigate = useNavigate();
	const [updateMultipleMaterialReservationsToCutStatusMutation] =
		useUpdateMultipleMaterialReservationsToCutStatusMutation();
	const [printCutTagMutation] = usePrintCutTagMutation();
	const { promptForBuilding } = usePrintLocation();
	// Stable key across retries so a lost response doesn't re-trigger a cut.
	const { getKey, rotate } = useIdempotencyKey();

	// Fetch operators directly in the modal to ensure data is available
	const userQuery: UserQueryInterface = useMemo(
		() => ({
			groups__name: 'Machine Operators',
		}),
		[]
	);

	const { data: usersDataFromQuery } = useGetUsersQuery(userQuery, {
		skip: false,
		refetchOnMountOrArgChange: true,
	});

	// Use fetched users if available, otherwise fall back to prop
	const userData = usersDataFromQuery?.results || userDataProp || [];

	const [checklistState, setChecklistState] = useState<{
		[key: string]: boolean;
	}>(() => {
		const initialChecklistState: { [key: string]: boolean } = {};
		const tempUniqueItems = new Map<string, MaterialReservation>();

		rawMaterialReservations.forEach((item) => {
			const groupKey = item.group_key || item.id;
			if (!tempUniqueItems.has(groupKey)) {
				tempUniqueItems.set(groupKey, { ...item, group_key: groupKey });
			}
		});

		Array.from(tempUniqueItems.keys()).forEach((key) => {
			initialChecklistState[`${key}_uniqueIdentifierMatches`] = false;
			initialChecklistState[`${key}_sizeMatches`] = false;
			initialChecklistState[`${key}_locationMatches`] = false;
			initialChecklistState[`${key}_otherMarkingsConsistent`] = false;
			initialChecklistState[`${key}_appearanceTypeConsistent`] = false;
			initialChecklistState[`${key}_conditionAcceptable`] = false;
			initialChecklistState[`${key}_certificateMatches`] = false;
		});
		return initialChecklistState;
	});

	const [isChecklistCompleted, setIsChecklistCompleted] =
		useState<boolean>(false);
	const [uniqueGroupItems, setUniqueGroupItems] = useState<
		MaterialReservation[]
	>([]);

	useEffect(() => {
		if (visible) {
			setOperatorTouched(false);
		}
	}, [visible]);

	useEffect(() => {
		if (visible && defaultUser != null) {
			setSelectedUser(defaultUser);
		}
	}, [visible, defaultUser]);

	useEffect(() => {
		if (!rawMaterialReservations.length) return;
		addGroupKeyToReservations();

		const tempUniqueItems = new Map<string, MaterialReservation>();

		rawMaterialReservations.forEach((item) => {
			const groupKey = item.group_key || item.id;
			if (!tempUniqueItems.has(groupKey)) {
				tempUniqueItems.set(groupKey, { ...item, group_key: groupKey });
			}
		});
		setUniqueGroupItems(Array.from(tempUniqueItems.values()));

		setCutMaterialReservationValues((prev) => {
			if (prev.length > 0) return prev;

			return rawMaterialReservations.map((item) => {
				const cutByValue = item.cut_by ?? selectedUser;
				const isSwiss =
					item.order_line_item?.partsbadger_production_method === 'Swiss';

				const initialCutQty = isSwiss
					? 1
					: item.material_cut_quantity - item.actual_cut_quantity;

				const predictedRemainingLength = getPredictedRemainingLength(
					Number(item.material_length?.current_length ?? 0),
					Number(item.material_cut_length),
					Number(item.saw_kerf),
					initialCutQty,
					item.order_line_item?.partsbadger_production_method,
					Number(item.reserved_length),
					item.required_trim_cut_length,
					item.material_cut_quantity
				);

				const remainingQty = item.material_cut_quantity - item.actual_cut_quantity;
				const fullConsumption = initialCutQty >= remainingQty;
				const autoScrap = fullConsumption
					? calculateScrapStatus(item, Number(predictedRemainingLength))
					: false;

				return {
					material_reservation: item.id,
					actual_cut_quantity: initialCutQty,
					remaining_length: predictedRemainingLength,
					cut_by: cutByValue,
					material_checklist_accepted: false,
					is_scrap: autoScrap,
				};
			});
		});
	}, [rawMaterialReservations, selectedUser]);

	useEffect(() => {
		const allGroupsChecked = uniqueGroupItems.every((groupItem) => {
			const keyPrefix = groupItem.group_key;
			return (
				checklistState[`${keyPrefix}_uniqueIdentifierMatches`] &&
				checklistState[`${keyPrefix}_sizeMatches`] &&
				checklistState[`${keyPrefix}_locationMatches`] &&
				checklistState[`${keyPrefix}_otherMarkingsConsistent`] &&
				checklistState[`${keyPrefix}_appearanceTypeConsistent`] &&
				checklistState[`${keyPrefix}_conditionAcceptable`] &&
				checklistState[`${keyPrefix}_certificateMatches`]
			);
		});
		setIsChecklistCompleted(allGroupsChecked);

		setCutMaterialReservationValues((prevValues) =>
			prevValues.map((item) => {
				addGroupKeyToReservations();
				const correspondingReservation = rawMaterialReservations.find(
					(res) => res.id === item.material_reservation
				);
				const groupKey = correspondingReservation?.group_key;
				if (
					groupKey &&
					checklistState[`${groupKey}_uniqueIdentifierMatches`] &&
					checklistState[`${groupKey}_sizeMatches`] &&
					checklistState[`${groupKey}_locationMatches`] &&
					checklistState[`${groupKey}_otherMarkingsConsistent`] &&
					checklistState[`${groupKey}_appearanceTypeConsistent`] &&
					checklistState[`${groupKey}_conditionAcceptable`] &&
					checklistState[`${groupKey}_certificateMatches`]
				) {
					return { ...item, material_checklist_accepted: true };
				}
				return { ...item, material_checklist_accepted: false };
			})
		);
	}, [checklistState, uniqueGroupItems, rawMaterialReservations]);

	const handleChecklistCheckboxChange = (groupKey: string, field: string) => {
		setChecklistState((prevState) => ({
			...prevState,
			[`${groupKey}_${field}`]: !prevState[`${groupKey}_${field}`],
		}));
	};

	/**
	 * Handle manual scrap checkbox change
	 * Allows operator to manually override automatic scrap detection.
	 * Scrap is only allowed when Enter actual cut quantity ≥ Remaining Qty (full consumption).
	 */
	const handleScrapCheckboxChange = (recordId: string, checked: boolean) => {
		setCutMaterialReservationValues((prevValues) =>
			prevValues.map((item) => {
				if (item.material_reservation !== recordId) return item;
				const record = rawMaterialReservations.find(
					(r) => r.id === recordId
				);
				const remainingQty = record
					? record.material_cut_quantity - record.actual_cut_quantity
					: 0;
				const scrapAllowed = item.actual_cut_quantity >= remainingQty;
				return {
					...item,
					is_scrap: checked && scrapAllowed,
				};
			})
		);
	};

	/**
	 * Scrap allowed only when Enter actual cut quantity ≥ Remaining Qty (full consumption).
	 */
	const isScrapAllowedForRecord = (
		record: MaterialReservation,
		enteredCutQty: number
	): boolean => {
		const remainingQty =
			record.material_cut_quantity - record.actual_cut_quantity;
		return enteredCutQty >= remainingQty;
	};

	const qualifiesAsScrap = (item: ICutItems): boolean =>
		qualifiesAsScrapForBulk({
			reservation: rawMaterialReservations.find(
				(r) => r.id === item.material_reservation
			),
			enteredCutQty: item.actual_cut_quantity,
			// An empty field must not read as a zero-length (i.e. scrap) remnant.
			remainingLength:
				item.remaining_length === '' || item.remaining_length == null
					? NaN
					: Number(item.remaining_length),
		});

	/**
	 * Scrap All: marks the rows that qualify on their own and never clears a row
	 * the operator already marked by hand.
	 */
	const handleScrapAll = () => {
		const total = cutMaterialReservationValues.length;
		const eligibleCount =
			cutMaterialReservationValues.filter(qualifiesAsScrap).length;
		const ineligibleCount = total - eligibleCount;

		// No rows qualify — inform instead of confirming
		if (eligibleCount === 0) {
			notification.warning({
				message: 'No rows eligible for Scrap',
				description:
					'Scrap All only marks remnants that are fully consumed and scrap-sized by the dimensional rule. Use the Scrap checkbox on a row to scrap anything else.',
				placement: 'topRight',
				duration: 5,
			});
			return;
		}

		const allEligible = eligibleCount === total;

		Modal.confirm({
			title: allEligible ? 'Mark all as Scrap?' : 'Mark as Scrap',
			content: (
				<div className="flex flex-col gap-2">
					{allEligible ? (
						<>
							<p>
								Are you sure you want to mark all{' '}
								<strong>{total}</strong> material(s) as scrap?
							</p>
							<p className="text-orange-600">
								⚠️ They will no longer be available for production.
							</p>
						</>
					) : (
						<>
							<p>
								<strong>{eligibleCount}</strong> of {total} row(s) will be
								marked as scrap.
							</p>
							<p>
								{ineligibleCount} row(s) left as they are: either the cut is
								partial (Enter actual cut quantity is less than Remaining Qty)
								or the remnant is still large enough to keep as inventory.
							</p>
							<p className="text-orange-600">
								⚠️ Scrapped materials cannot be used for production.
							</p>
						</>
					)}
				</div>
			),
			okText: allEligible ? 'Yes, Mark as Scrap' : 'Yes, Mark Eligible',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				setCutMaterialReservationValues((prevValues) =>
					prevValues.map((item) => ({
						...item,
						is_scrap: item.is_scrap || qualifiesAsScrap(item),
					}))
				);

				notification.success({
					message: allEligible
						? 'All marked as Scrap'
						: 'Eligible rows marked as Scrap',
					description: allEligible
						? `${total} material(s) marked as scrap.`
						: `${eligibleCount} of ${total} row(s) marked as scrap.`,
					placement: 'topRight',
					duration: 3,
				});
			},
		});
	};

	// A bar can go on quality hold or be scrapped after it was reserved, so the
	// queue can offer a cut that should no longer happen. One row deep in a
	// table is easy to miss, so the affected bars are also called out above it.
	const blockedBars = useMemo(() => {
		const seen = new Map<string, { id: string; friendlyId: string; reason: string }>();
		rawMaterialReservations.forEach((reservation) => {
			const reason = barBlockingReason(reservation?.material_length?.status);
			const id = reservation?.material_length?.id;
			if (!reason || !id || seen.has(id)) return;
			seen.set(id, {
				id,
				friendlyId: reservation.material_length?.friendly_id || 'Unknown bar',
				reason,
			});
		});
		return Array.from(seen.values());
	}, [rawMaterialReservations]);

	const tableDescriptionColumns: any = [
		{
			title: 'Unique Id',
			width: 80,
			render: (record: MaterialReservation) => {
				const blockingReason = barBlockingReason(
					record?.material_length?.status
				);
				return (
					<div className="flex flex-col gap-1">
						<a
							onClick={() => {
								navigate(
									`/material/materials?search=${record?.material_length?.material?.id}`
								);
							}}
						>
							{record?.material_length?.friendly_id}
						</a>
						{blockingReason ? (
							<Tag
								data-testid="fastlane-cut-bar-warning"
								color="red"
								style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}
							>
								{blockingReason}
							</Tag>
						) : null}
					</div>
				);
			},
		},
		{
			title: 'Current Length (IN)',
			width: 80,
			render: (record: MaterialReservation) =>
				record?.material_length?.current_length,
		},
		{
			title: 'Remaining Qty',
			width: 80,
			render: (record: MaterialReservation) =>
				record?.material_cut_quantity - record.actual_cut_quantity,
		},
		{
			title: 'Enter actual cut quantity',
			width: 80,
			render: (record: MaterialReservation) => {
				const isSwiss =
					record.order_line_item?.partsbadger_production_method === 'Swiss';
				const defaultQty = isSwiss
					? 1
					: record.material_cut_quantity - record.actual_cut_quantity;

				return (
					<InputNumber
						placeholder={`${defaultQty}`}
						size="large"
						defaultValue={defaultQty}
						onFocus={(e) => e.target.select()}
						min={0}
						disabled={isSwiss}
						style={{ width: '100%' }}
						onChange={(value) => {
							// Validate input value
							if (value === null || value === undefined || value < 0) {
								return; // Ignore invalid values
							}

							const validValue = Math.max(0, Number(value)); // Ensure non-negative

							const predictedRemainingLength = getPredictedRemainingLength(
								Number(record.material_length.current_length),
								Number(record.material_cut_length),
								Number(record.saw_kerf),
								validValue,
								record.order_line_item?.partsbadger_production_method,
								Number(record.reserved_length),
								record.required_trim_cut_length,
								record.material_cut_quantity
							);

							const updated_materialReservations: MaterialReservation[] =
								rawMaterialReservations.map((item) => {
									if (item.id === record.id) {
										return {
											...item,
											predicted_remaining_length: Number(
												predictedRemainingLength
											),
										};
									}
									return item;
								});

							overrideMaterialReservations(updated_materialReservations);

							const remainingQty =
								record.material_cut_quantity - record.actual_cut_quantity;
							// Scrap only when full consumption: Enter actual cut quantity ≥ Remaining Qty
							const scrapAllowed = validValue >= remainingQty;
							const autoScrap = scrapAllowed
								? calculateScrapStatus(
										record,
										Number(predictedRemainingLength)
									)
								: false;

							setCutMaterialReservationValues(
								cutMaterialReservationValues.map((item) => {
									if (item.material_reservation === record.id) {
										return {
											...item,
											actual_cut_quantity: validValue,
											remaining_length: predictedRemainingLength,
											is_scrap: autoScrap, // Auto-update scrap status
										};
									}
									return item;
								})
							);
						}}
					/>
				);
			},
		},
		{
			title: 'Remaining length (IN):',
			width: 80,
			render: (record: MaterialReservation) => {
				// Show the trim-aware predicted remaining length that will
				// actually be submitted (PTB-5904). Fall back to the backend
				// predicted_remaining_length only when no local value exists yet.
				const computedRemaining = cutMaterialReservationValues.find(
					(i) => i.material_reservation === record.id
				)?.remaining_length;
				const displayRemaining =
					computedRemaining ??
					record?.predicted_remaining_length?.toString();
				return (
					<InputNumber
						placeholder={displayRemaining}
						size="large"
					onFocus={(e) => e.target.select()}
					style={{ width: '100%' }}
					min={0}
					onChange={(value) => {
						// Validate input value
						if (value === null || value === undefined || value < 0) {
							return; // Ignore invalid values
						}

						const validValue = Math.max(0, Number(value)); // Ensure non-negative

						const cutItem = cutMaterialReservationValues.find(
							(i) => i.material_reservation === record.id
						);
						const remainingQty =
							record.material_cut_quantity - record.actual_cut_quantity;
						const scrapAllowed =
							(cutItem?.actual_cut_quantity ?? 0) >= remainingQty;
						const autoScrap = scrapAllowed
							? calculateScrapStatus(record, validValue)
							: false;

						setCutMaterialReservationValues(
							cutMaterialReservationValues.map((item) => {
								if (item.material_reservation === record.id) {
									return {
										...item,
										remaining_length: validValue.toString(),
										is_scrap: autoScrap,
									};
								}
								return item;
							})
						);
					}}
				/>
			);
			},
		},
		// Scrap column: lock only when qualifies by rule; user can uncheck if they marked it manually
		{
			title: 'Scrap',
			width: 60,
			render: (record: MaterialReservation) => {
				const cutItem = cutMaterialReservationValues.find(
					(item) => item.material_reservation === record.id
				);
				const remainingQty =
					record.material_cut_quantity - record.actual_cut_quantity;
				const enteredQty = cutItem?.actual_cut_quantity ?? 0;
				const scrapAllowed = isScrapAllowedForRecord(record, enteredQty);
				const checked = cutItem?.is_scrap ?? false;
				// Lock only when checked AND qualifies by rule; user-marked scrap can be unchecked
				const remainingLength = Number(cutItem?.remaining_length ?? 0);
				const qualifiesAsScrapByRule =
					scrapAllowed && calculateScrapStatus(record, remainingLength);
				const isScrapLocked = checked && qualifiesAsScrapByRule;

				const checkbox = (
					<Checkbox
						checked={checked}
						disabled={!scrapAllowed || isScrapLocked}
						onChange={(e) =>
							handleScrapCheckboxChange(record.id, e.target.checked)
						}
					/>
				);

				return scrapAllowed
					? isScrapLocked
						? (
								<Tooltip title="This material qualifies as scrap by rule and cannot be unmarked">
									<span>{checkbox}</span>
								</Tooltip>
							)
						: checkbox
					: (
							<Tooltip title="Scrap only when Enter actual cut quantity ≥ Remaining Qty">
								<span>{checkbox}</span>
							</Tooltip>
						);
			},
		},
	];

	const addGroupKeyToReservations = () => {
		rawMaterialReservations = rawMaterialReservations.map((item) => {
			if (!('group_key' in item)) {
				return {
					...item,
					group_key: item.id,
				};
			}
			return item;
		});
	};

	async function printCutTag(group_key: string, material_event_id?: string) {
		promptForBuilding(
			async (selectedBuildingId) => {
				await printCutTagMutation({
					group_key: group_key,
					material_event_id: material_event_id,
					...(selectedBuildingId ? { building_id: selectedBuildingId } : {}),
				})
					.unwrap()
					.then(() => {
						notification.success({
							duration: 10,
							message: 'Print Successful',
							description: 'Your print was successfully sent to the printer.',
							placement: 'topRight',
						});
					})
					.catch((error) => {
						console.error('error', error);
						notification.error({
							duration: 10,
							message: 'Error',
							description: `Error encountered while attempting to Print.`,
							placement: 'topRight',
						});
					});
			},
			{ description: 'Select the building to print the cut tag at.' },
		);
	}

	function handleUpdateMultipleReservationToCutStatus(
		materialReservationValues: ICutItems[],
		print: boolean
	) {
		// `group_key` is the identifier the backend uses to print the cut tag.
		// The SawCutQueue path manually injects it on every reservation; other
		// entry points (OLI page, Material Inventory page) rely on the
		// serializer to expose it. As a defensive fallback we derive it from
		// the reservation fields using the same formula as
		// `MaterialReservation.get_group_key()` and the SQL annotation in
		// `get_queryset_grouped`. Without this, "Save and Print" silently
		// skipped printing — which also hid the building picker.
		const groupKey = deriveGroupKey(rawMaterialReservations[0]);

		if (!selectedUser) {
			notification.error({
				duration: 5,
				message: 'User Not Selected',
				description: 'Please select a user to perform cutting actions.',
				placement: 'topRight',
			});
			return;
		}

		if (setRadioButtonLoading) {
			setRadioButtonLoading(true);
		}
		setIsCutting(true);

		if (!isChecklistCompleted) {
			notification.error({
				duration: 5,
				message: 'Checklist Incomplete',
				description:
					'Please complete all items in the material acceptance checklist.',
				placement: 'topRight',
			});
			setIsCutting(false);
			if (setRadioButtonLoading) {
				setRadioButtonLoading(false);
			}
			return;
		}

		// Update all cut_by values with selected user; only send is_scrap when full consumption
		const updatedValues = materialReservationValues.map((item) => {
			const record = rawMaterialReservations.find(
				(r) => r.id === item.material_reservation
			);
			const remainingQty = record
				? record.material_cut_quantity - record.actual_cut_quantity
				: 0;
			const scrapAllowed = item.actual_cut_quantity >= remainingQty;
			return {
				...item,
				cut_by: selectedUser,
				is_scrap: (item.is_scrap || false) && scrapAllowed,
			};
		});

		updateMultipleMaterialReservationsToCutStatusMutation({
			values: updatedValues,
			idempotencyKey: getKey(),
		})
			.unwrap()
			.then((response) => {
				const material_event_id = response.material_event_id;
				const updatedMaterialLengths: Array<{
					id: string;
					friendly_id: string;
					current_length: string;
					status: string;
					inventory_location_display_name: string;
					overflow_message?: string;
				}> = response.material_lengths || [];

				if (print && groupKey) {
					printCutTag(groupKey, material_event_id);
				}

				// Save groupKey for print function in summary modal
				if (groupKey) {
					setSavedGroupKey(groupKey);
				}

				// Build a lookup map from material length id to the updated data from the backend
				const materialLengthMap = new Map(
					updatedMaterialLengths.map((ml) => [ml.id, ml])
				);

				// Prepare and show Material Locations Summary Modal
				const firstReservation = rawMaterialReservations[0];

				const getMaterialInfoFromReservation = (reservation: any) => {
					const mat = reservation?.material_length?.material;
					return {
						type: mat?.type?.display_name || reservation?.material_type_display_name || 'N/A',
						width: reservation?.material_width,
						height: reservation?.material_height,
						outer_diameter: reservation?.material_outer_diameter,
						inner_diameter: reservation?.material_inner_diameter,
						wall_thickness: reservation?.material_wall_thickness,
						shape: reservation?.material_shape_type,
					};
				};

				const summaryData = {
					materialInfo: getMaterialInfoFromReservation(firstReservation),
					lengths: rawMaterialReservations.map((reservation) => {
						const materialLengthId =
							reservation.material_length?.id || reservation.id;
						const backendData = materialLengthMap.get(materialLengthId);
						const perLengthMaterialInfo = getMaterialInfoFromReservation(reservation);

						if (backendData) {
							return {
								id: backendData.id,
								friendlyId: backendData.friendly_id || 'N/A',
								location:
									backendData.inventory_location?.display_name ??
									backendData.inventory_location_display_name ??
									'Unknown',
								length: Number(backendData.current_length),
								isScrapped: backendData.status === 'Scrapped',
								overflowMessage: backendData.overflow_message || undefined,
								materialInfo: perLengthMaterialInfo,
							};
						}

						return {
							id: materialLengthId,
							friendlyId: reservation.material_length?.friendly_id || 'N/A',
							location:
								reservation.material_length?.inventory_location?.display_name ??
								'Unknown',
							length: Number(reservation.material_length?.current_length || 0),
							isScrapped: reservation.material_length?.status === 'Scrapped',
							materialInfo: perLengthMaterialInfo,
						};
					}),
				};

				setLocationSummaryData(summaryData);
				setShowLocationSummary(true);

				// Success — next cut should use a fresh idempotency key.
				rotate();
			})
			.catch((error) => {
				notification.error({
					duration: 10,
					message: 'Error Cutting Material',
					description: getApiErrorMessage(error),
					placement: 'topRight',
				});
				// The cut may have partially/fully committed server-side even when
				// the client sees an error (e.g. a proxy timeout). Refetch so the
				// modal reflects the real state and the operator can't replay a
				// stale payload.
				refetch();
			})
			.finally(() => {
				if (setRadioButtonLoading) {
					setRadioButtonLoading(false);
				}
				setIsCutting(false);
			});
	}

	return (
		<>
			<Modal
				width={900}
				centered
				open={visible && !showLocationSummary}
				onCancel={() => {
					setOperatorTouched(false);
					setShowLocationSummary(false);
					onCancel();
				}}
				footer={
					<div className={'flex flex-row gap-2 justify-end items-center'}>
						<div>
							<Button
								className="mr-2"
								loading={isCutting}
								onClick={() => onCancel()}
							>
								Cancel
							</Button>
							<Button
								className="mr-2"
								form="create-manufacturing-process-form"
								type="primary"
								loading={isCutting}
								onClick={() => {
									handleUpdateMultipleReservationToCutStatus(
										cutMaterialReservationValues,
										false
									);
								}}
								disabled={!isChecklistCompleted || !selectedUser}
							>
								Save
							</Button>

							<Button
								form="create-manufacturing-process-form"
								type="primary"
								loading={isCutting}
								onClick={() => {
									handleUpdateMultipleReservationToCutStatus(
										cutMaterialReservationValues,
										true
									);
								}}
								disabled={!isChecklistCompleted || !selectedUser}
							>
								Save and Print
							</Button>
						</div>
					</div>
				}
				cancelButtonProps={{ size: 'large' }}
				okButtonProps={{ size: 'large' }}
				title="Cut Material"
			>
				<div
					data-testid="fastlane-cut-reservation-modal"
					data-ready={!loadingMaterialReservations ? 'true' : 'false'}
					data-material-reservation-id={rawMaterialReservations[0]?.id || ''}
					data-cut-group-key={rawMaterialReservations[0]?.group_key || ''}
					className="flex flex-col gap-4"
				>
					<div>
						This will subtract the reserved length from the current length in
						the database.
					</div>

					{blockedBars.length ? (
						<Alert
							data-testid="fastlane-cut-blocked-bars-alert"
							type="warning"
							showIcon
							message={
								blockedBars.length === 1
									? 'One bar in this cut should not be cut yet'
									: `${blockedBars.length} bars in this cut should not be cut yet`
							}
							description={
								<div className="flex flex-col gap-1">
									{blockedBars.map((bar) => (
										<span key={bar.id}>
											<strong>{bar.friendlyId}</strong> — {bar.reason}
										</span>
									))}
									<span>
										Cutting is not blocked, but check with a lead first: this
										material may be rejected or already written off.
									</span>
								</div>
							}
						/>
					) : null}

					<OperatorPinField
						userData={userData}
						expectedGroups={['Machine Operators']}
						value={selectedUser}
						showError={operatorTouched}
						onResolved={(userId) => {
							setOperatorTouched(true);
							setSelectedUser(userId);
							setCutMaterialReservationValues((prevValues) =>
								prevValues.map((item) => ({
									...item,
									cut_by: userId ?? null,
								}))
							);
						}}
					/>

					{uniqueGroupItems.map((groupItem) => (
						<MaterialChecklist
							key={groupItem.group_key || `no_group_key_${groupItem.id}`}
							groupItem={groupItem}
							checklistState={checklistState}
							handleChecklistCheckboxChange={handleChecklistCheckboxChange}
						/>
					))}

					<div>
						{/* Scrap All button */}
						<div className="flex justify-end mb-2">
							<Button type="default" onClick={handleScrapAll} size="small">
								Scrap All
							</Button>
						</div>

						<div className="flex flex-col mt-2">
							<CustomResponsiveTable
								columns={tableDescriptionColumns}
								data={rawMaterialReservations}
								pagination={{ pageSize: 20 }}
								loading={loadingMaterialReservations}
								emptyTable={[]}
								excludeWidthColumn={true}
								rowClassName={(record, index) =>
									index % 2 === 0 ? 'table-white-row' : 'table-dark-row'
								}
								size="small"
								tableMaxWidth={700}
							/>
						</div>
					</div>
				</div>
			</Modal>

			{/* Material Locations Summary Modal */}
			<MaterialLocationsSummaryModal
				visible={showLocationSummary}
				onComplete={() => {
					setShowLocationSummary(false);
					if (savedGroupKey && handleFetchReservationsById) {
						handleFetchReservationsById(savedGroupKey);
					}
					refetch();
					onCancel();
				}}
				materialInfo={locationSummaryData.materialInfo}
				lengths={locationSummaryData.lengths}
				title="Material Locations Summary"
				source="cutting"
			/>
		</>
	);
};

export default CutMaterialModal;
